import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

//import material ui
import {
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  Paper,
  CardHeader,
  Typography,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Divider,
  Tooltip,
  Pagination,
  TablePagination,
  Fab,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
//Icons
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

//project components
import config from "../../../config";

//third party
import useAxiosAuth from "../../../hooks/useAxiosAuth";
import { Box } from "@mui/system";

import NoDataFound from "../../../components/NoDataFound";
import {
  convertDateToLocal,
  convertDateToLocalReverse,
} from "../../../utils/date-operations";

const PlacementRecords = () => {
  //Constants
  const navigate = useNavigate();
  const axiosAuth = useAxiosAuth();
  const pageSize = 50;
  const currentDate = new Date();
  const startOfMonth = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  }, []);
  const endOfMonth = useMemo(() => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  }, []);
  //States

  const [placementRecords, setPlacementRecords] = useState();

  const [selectedEmployer, setSelectedEmployer] = useState();

  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState(-1);

  const [fromDate, setFromDate] = useState(
    convertDateToLocalReverse(startOfMonth)
  );
  const [toDate, setToDate] = useState(convertDateToLocalReverse(endOfMonth));

  // Method to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 0: // Applied
        return "#42A5F5";
      case 1: // Selected For Interview
        return "#FFA726";
      case 3: // Attended
        return "#26A69A";
      case 4: // Not Attended
        return "#EF5350";
      case 2: // Placed
        return "#66BB6A";
      default:
        return "inherit";
    }
  };

  //Methods
  const getPlacementRecords = (skip) => {
    let data = {
      searchText: searchText,
      fromDate: fromDate,
      toDate: toDate,
      jobApplicationStatus: status,
      skip: skip,
      take: pageSize,
    };
    axiosAuth
      .post("/api/Candidates/placed-list", data)
      .then((response) => {
        setPlacementRecords(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  //UseEffects
  useEffect(() => {
    getPlacementRecords(0);
  }, [searchText, status, fromDate, toDate]);

  return (
    <Grid maxWidth="xl" sx={{ marginX: "auto", paddingY: 4, paddingX: 2 }}>
      <Card elevation={3}>
        <Grid
          sx={{
            background: "linear-gradient(to right,#4a7dcf 0%, #2a569f 100%)",
            paddingX: 2,
            paddingY: 1,
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" color="white.main">
              Placements
            </Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            {placementRecords !== undefined && (
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ paddingX: 2, paddingTop: 2 }}
              >
                {/* Search Field */}
                <Grid item xs={12} sm={5} md={5}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by name or mobile number"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchText !== "" && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setSearchText("");
                              getPlacementRecords(0);
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                  />
                </Grid>

                {/* From Date */}
                <Grid item xs={12} sm={2} md={2}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>

                {/* To Date */}
                <Grid item xs={12} sm={2} md={2}>
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>

                {/* Status Dropdown */}
                <Grid item xs={12} sm={3} md={3}>
                  <TextField
                    label="Status"
                    select
                    size="small"
                    fullWidth
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                    }}
                  >
                    <MenuItem value={-1}>All</MenuItem>
                    <MenuItem value={0}>Applied</MenuItem>
                    <MenuItem value={1}>Selected For Interview</MenuItem>
                    <MenuItem value={3}>Attended</MenuItem>
                    <MenuItem value={4}>Not Attended</MenuItem>
                    <MenuItem value={2}>Placed</MenuItem>
                  </TextField>
                </Grid>

                {/* Pagination */}
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Typography>Total: {placementRecords.count}</Typography>
                    <Pagination
                      count={Math.ceil(placementRecords.count / pageSize)}
                      variant="outlined"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                      color="primary"
                      onChange={(event, page) => {
                        getPlacementRecords((page - 1) * pageSize);
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            {placementRecords != undefined ? (
              placementRecords.candidates &&
              placementRecords.candidates.length > 0 ? (
                <Box>
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      flexDirection: "column",
                    }}
                  >
                    {placementRecords.candidates?.map((employer, index) => {
                      return (
                        <Card
                          key={index}
                          variant="outlined"
                          sx={{
                            margin: 1,
                            padding: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: "#F5F5F5",
                          }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="h6">
                              <b>Name:</b> {employer.name}
                            </Typography>
                            <Typography>
                              <b>Email:</b> {employer.email}
                            </Typography>
                            <Typography>
                              <b>Phone No.:</b> {employer.phoneNumber}
                            </Typography>
                            <Typography>
                              <b>City:</b> {employer.city}
                            </Typography>
                            <Typography>
                              <b>Company Name:</b> {employer.employerName}
                            </Typography>
                            <Typography>
                              <b>Status:</b>{" "}
                              <Typography
                                variant="h6"
                                component="span"
                                color={getStatusColor(
                                  employer.jobApplicationStatus
                                )}
                              >
                                {employer.jobApplicationStatusDisplay}
                              </Typography>
                            </Typography>
                          </Stack>
                        </Card>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "flex" } }}>
                    <TableContainer sx={{ padding: 2 }}>
                      <Table
                        sx={{
                          borderWidth: 1,
                          border: "solid",
                          borderColor: "#f2f2f2",
                        }}
                        size="small"
                      >
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#e6eeff" }}>
                            <TableCell sx={{ width: 50 }}>#</TableCell>
                            <TableCell align="left">Candidate Name</TableCell>
                            <TableCell align="left" sx={{ width: 120 }}>
                              Email
                            </TableCell>
                            <TableCell align="left" sx={{ width: 120 }}>
                              Phone No.
                            </TableCell>
                            <TableCell align="left" sx={{ width: 120 }}>
                              City
                            </TableCell>
                            <TableCell align="left" sx={{ width: 150 }}>
                              Company Name
                            </TableCell>
                            <TableCell align="left" sx={{ width: 150 }}>
                              Status
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ width: 100 }}
                            ></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {placementRecords.candidates?.map((row, index) => (
                            <TableRow key={row.id}>
                              <TableCell component="th" scope="row">
                                {row.index}
                              </TableCell>
                              <TableCell align="left">
                                <Typography variant="h6">
                                  {`${row.salutation}
                                  ${row.firstName}
                                 ${row.middleName}
                                 ${row.lastName}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="left">{row.email}</TableCell>
                              <TableCell align="left">
                                {row.phoneNumber}
                              </TableCell>
                              <TableCell align="left">{row.city}</TableCell>
                              <TableCell align="left">
                                {row.employerName}
                              </TableCell>
                              <TableCell align="left">
                                <Typography
                                  variant="h6"
                                  color={getStatusColor(
                                    row.jobApplicationStatus
                                  )}
                                >
                                  {row.jobApplicationStatusDisplay}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit">
                                  <IconButton
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => {
                                      navigate(
                                        "/admin/jobpost?ref=" +
                                          row?.jobPostId +
                                          "&prePage=/admin/placement-records"
                                      );
                                    }}
                                  >
                                    <LaunchIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ padding: 2 }}>
                  <Typography variant="h5" color="textSecondary">
                    <NoDataFound />
                  </Typography>
                </Box>
              )
            ) : (
              <Box
                sx={{ display: "flex", justifyContent: "center", marginY: 5 }}
              >
                <Stack
                  direction="column"
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h5" textAlign="center">
                    Loading Placement Records...
                  </Typography>
                  <CircularProgress />
                </Stack>
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            {placementRecords != undefined ? (
              <Box
                sx={{
                  paddingX: 2,
                  marginBottom: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Pagination
                  count={Math.ceil(placementRecords.count / pageSize)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  color="primary"
                  sx={{ marginTop: 2 }}
                  onChange={(event, page) => {
                    getPlacementRecords((page - 1) * pageSize);
                  }}
                />
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};

export default PlacementRecords;
